/*!

=========================================================
* Argon Dashboard React - v1.2.4
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import {useEffect, useState} from "react";
// node.js library that concatenates classes (strings)
import classnames from "classnames";
// react plugin used to create charts
import { Line, Bar } from "react-chartjs-2";
// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  NavItem,
  NavLink,
  Nav,
  Progress,
  Table,
  Container,
  Row,
  Col,
} from "reactstrap";

// core components
import {
  chartOptions,
  parseOptions,
  chartExample1,
  chartExample2,
} from "variables/charts.js";

import Header from "components/Headers/Header.js";
import axios from "axios";
import {getCookie} from "../tools/tools";
import colors from "chart.js";
import {useTranslation} from "react-i18next";

const Index = (props) => {
  const [activeNav, setActiveNav] = useState(1);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [weeklyStats, setWeeklyStats] = useState([]);
  const [calculatorStats, setCalculatorStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get('/api/calculators/stats');
        const {monthly_stats, weekly_stats, calculator_monthly_stats} = response.data;

        setMonthlyStats(monthly_stats);
        setWeeklyStats(weekly_stats);
        setCalculatorStats(calculator_monthly_stats);
      } catch (err) {
        console.error('Ошибка при загрузке статистики:', err);
        setError('Не удалось загрузить статистику.');
      }
    };

    fetchStats();
  }, []);

  // Функция для форматирования месяца
  const formatMonth = (isoDate) => {
    const date = new Date(isoDate);
    const year = date.getFullYear();
    const month = (`0${date.getMonth() + 1}`).slice(-2); // Добавляем ведущий ноль
    return `${year}-${month}`; // Формат "YYYY-MM"
  };

  // Функция для форматирования недели
  const formatWeek = (isoDate) => {
    const date = new Date(isoDate);
    const year = date.getFullYear();

    // Вычисляем номер недели по ISO-8601
    const onejan = new Date(year, 0, 1);
    const millisecsInDay = 86400000;
    const dayOfWeek = onejan.getDay() || 7; // getDay() возвращает 0 для воскресенья
    const weekNumber = Math.ceil(((date - onejan) / millisecsInDay + onejan.getDay() - 1) / 7);

    return `${year}-W${weekNumber.toString().padStart(2, '0')}`; // Формат "YYYY-W##"
  };


   // Подготовка данных для месячной статистики
  const monthlyChartData = {
    labels: monthlyStats.map(stat => stat.month),
    datasets: [
      {
        label: 'Успешные',
        data: monthlyStats.map(stat => stat.successful),
        borderColor: 'rgba(130, 202, 157, 1)',
        backgroundColor: 'rgba(130, 202, 157, 0.5)',
        fill: false,
      },
      {
        label: 'Неудачные',
        data: monthlyStats.map(stat => stat.failed),
        borderColor: 'rgba(255, 115, 0, 1)',
        backgroundColor: 'rgba(255, 115, 0, 0.5)',
        fill: false,
      },
    ],
  };

  const monthlyChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Статистика по месяцам за последний год',
      },
      scales: {
        y: { beginAtZero: false,       min: 0,
        max: 5},
      },
    },
  };

  // Подготовка данных для недельной статистики
  const weeklyChartData = {
    labels: weeklyStats.map(stat => stat.week),
    datasets: [
      {
        label: 'Успешные',
        data: weeklyStats.map(stat => stat.successful),
        borderColor: 'rgba(136, 132, 216, 1)',
        backgroundColor: 'rgba(136, 132, 216, 0.5)',
        fill: false,
      },
      {
        label: 'Неудачные',
        data: weeklyStats.map(stat => stat.failed),
        borderColor: 'rgba(255, 0, 0, 1)',
        backgroundColor: 'rgba(255, 0, 0, 0.5)',
        fill: false,
      },
    ],
  };

  const weeklyChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Статистика по неделям за последний год',
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Неделя',
          },
        },
        y: {
          beginAtZero: true,
          ticks: {
            precision: 0,
            callback: function (value) {
              return value;
            },
          },
          title: {
            display: true,
            text: 'Количество вычислений',
          },
          grid: {
            display: true,
            color: 'rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
  };


  if (window.Chart) {
    parseOptions(Chart, chartOptions());
  }

  const toggleNavs = (e, index) => {
    e.preventDefault();
    setActiveNav(index);
  };

  // Подготовка данных для графика по калькуляторам
  const getCalculatorChartData = () => {
    const labels = ['Z calc 1', 'Z calc 2', 'Simple FEM', 'Hard FEM'];
    const successfulData = [];
    const failedData = [];

    for (const [calcId, data] of Object.entries(calculatorStats)) {
      const calcLabels = Object.keys(data).sort();

      // Суммируем успешные и неудачные вычисления
      let totalSuccessful = 0;
      let totalFailed = 0;

      calcLabels.forEach(month => {
        totalSuccessful += data[month]?.successful || 0;
        totalFailed += data[month]?.failed || 0;
      });

      successfulData.push(totalSuccessful);
      failedData.push(totalFailed);
    }

    return {
      data: {
        labels,
        datasets: [
          {
            label: 'Успешные вычисления',
            data: successfulData,
            backgroundColor: 'rgba(136, 132, 216, 0.5)',
            maxBarThickness: 10,
          },
          {
            label: 'Неудачные вычисления',
            data: failedData,
            backgroundColor: 'rgba(255, 0, 0, 0.5)',
            maxBarThickness: 10,
          },
        ],
      },
    };
  };
  const { t, i18n } = useTranslation();

  return (
    <>
      <Header />
      {/* Page content */}
      <Container className="mt--7" fluid>
        <Row>
          <Col className="mb-5 mb-xl-0" xl="8">
            <Card className="bg-gradient-default shadow">
              <CardHeader className="bg-transparent">
                <Row className="align-items-center">
                  <div className="col">
                    <h6 className="text-uppercase text-light ls-1 mb-1">
                      {t('CalcStats')}
                    </h6>
                    <h2 className="text-white mb-0">{t('CalcTimes')}</h2>
                  </div>
                  <div className="col">
                    <Nav className="justify-content-end" pills>
                      <NavItem>
                        <NavLink
                          className={classnames("py-2 px-3", {
                            active: activeNav === 1,
                          })}
                          href="#pablo"
                          onClick={(e) => toggleNavs(e, 1)}
                        >
                          <span className="d-none d-md-block">Month</span>
                          <span className="d-md-none">M</span>
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames("py-2 px-3", {
                            active: activeNav === 2,
                          })}
                          data-toggle="tab"
                          href="#pablo"
                          onClick={(e) => toggleNavs(e, 2)}
                        >
                          <span className="d-none d-md-block">Week</span>
                          <span className="d-md-none">W</span>
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </div>
                </Row>
              </CardHeader>
              <CardBody>
                {/* Chart */}
                <div className="chart">
                  {activeNav === 1 && (<Line data={monthlyChartData} options={monthlyChartOptions} getDatasetAtEvent={(e) => console.log(e)}/>)}
                  {activeNav === 2 && (<Line data={weeklyChartData} options={weeklyChartOptions} getDatasetAtEvent={(e) => console.log(e)}/>)}
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl="4">
            <Card className="shadow">
              <CardHeader className="bg-transparent">
                <Row className="align-items-center">
                  <div className="col">
                    <h6 className="text-uppercase text-muted ls-1 mb-1">
                      {t('CalculatorStats')}
                    </h6>
                    <h2 className="mb-0">{t('CalcYear')}</h2>
                  </div>
                </Row>
              </CardHeader>
              <CardBody>
                {/* Chart */}
                <div className="chart">
                  <Bar data={getCalculatorChartData().data} options={getCalculatorChartData().options} />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
        {/*<Row className="mt-5">*/}
        {/*  <Col className="mb-5 mb-xl-0" xl="8">*/}
        {/*    <Card className="shadow">*/}
        {/*      <CardHeader className="border-0">*/}
        {/*        <Row className="align-items-center">*/}
        {/*          <div className="col">*/}
        {/*            <h3 className="mb-0">Page visits</h3>*/}
        {/*          </div>*/}
        {/*          <div className="col text-right">*/}
        {/*            <Button*/}
        {/*              color="primary"*/}
        {/*              href="#pablo"*/}
        {/*              onClick={(e) => e.preventDefault()}*/}
        {/*              size="sm"*/}
        {/*            >*/}
        {/*              See all*/}
        {/*            </Button>*/}
        {/*          </div>*/}
        {/*        </Row>*/}
        {/*      </CardHeader>*/}
        {/*      <Table className="align-items-center table-flush" responsive>*/}
        {/*        <thead className="thead-light">*/}
        {/*          <tr>*/}
        {/*            <th scope="col">Page name</th>*/}
        {/*            <th scope="col">Visitors</th>*/}
        {/*            <th scope="col">Unique users</th>*/}
        {/*            <th scope="col">Bounce rate</th>*/}
        {/*          </tr>*/}
        {/*        </thead>*/}
        {/*        <tbody>*/}
        {/*          <tr>*/}
        {/*            <th scope="row">/argon/</th>*/}
        {/*            <td>4,569</td>*/}
        {/*            <td>340</td>*/}
        {/*            <td>*/}
        {/*              <i className="fas fa-arrow-up text-success mr-3" /> 46,53%*/}
        {/*            </td>*/}
        {/*          </tr>*/}
        {/*          <tr>*/}
        {/*            <th scope="row">/argon/index.html</th>*/}
        {/*            <td>3,985</td>*/}
        {/*            <td>319</td>*/}
        {/*            <td>*/}
        {/*              <i className="fas fa-arrow-down text-warning mr-3" />{" "}*/}
        {/*              46,53%*/}
        {/*            </td>*/}
        {/*          </tr>*/}
        {/*          <tr>*/}
        {/*            <th scope="row">/argon/charts.html</th>*/}
        {/*            <td>3,513</td>*/}
        {/*            <td>294</td>*/}
        {/*            <td>*/}
        {/*              <i className="fas fa-arrow-down text-warning mr-3" />{" "}*/}
        {/*              36,49%*/}
        {/*            </td>*/}
        {/*          </tr>*/}
        {/*          <tr>*/}
        {/*            <th scope="row">/argon/tables.html</th>*/}
        {/*            <td>2,050</td>*/}
        {/*            <td>147</td>*/}
        {/*            <td>*/}
        {/*              <i className="fas fa-arrow-up text-success mr-3" /> 50,87%*/}
        {/*            </td>*/}
        {/*          </tr>*/}
        {/*          <tr>*/}
        {/*            <th scope="row">/argon/profile.html</th>*/}
        {/*            <td>1,795</td>*/}
        {/*            <td>190</td>*/}
        {/*            <td>*/}
        {/*              <i className="fas fa-arrow-down text-danger mr-3" />{" "}*/}
        {/*              46,53%*/}
        {/*            </td>*/}
        {/*          </tr>*/}
        {/*        </tbody>*/}
        {/*      </Table>*/}
        {/*    </Card>*/}
        {/*  </Col>*/}
        {/*  <Col xl="4">*/}
        {/*    <Card className="shadow">*/}
        {/*      <CardHeader className="border-0">*/}
        {/*        <Row className="align-items-center">*/}
        {/*          <div className="col">*/}
        {/*            <h3 className="mb-0">Social traffic</h3>*/}
        {/*          </div>*/}
        {/*          <div className="col text-right">*/}
        {/*            <Button*/}
        {/*              color="primary"*/}
        {/*              href="#pablo"*/}
        {/*              onClick={(e) => e.preventDefault()}*/}
        {/*              size="sm"*/}
        {/*            >*/}
        {/*              See all*/}
        {/*            </Button>*/}
        {/*          </div>*/}
        {/*        </Row>*/}
        {/*      </CardHeader>*/}
        {/*      <Table className="align-items-center table-flush" responsive>*/}
        {/*        <thead className="thead-light">*/}
        {/*          <tr>*/}
        {/*            <th scope="col">Referral</th>*/}
        {/*            <th scope="col">Visitors</th>*/}
        {/*            <th scope="col" />*/}
        {/*          </tr>*/}
        {/*        </thead>*/}
        {/*        <tbody>*/}
        {/*          <tr>*/}
        {/*            <th scope="row">Facebook</th>*/}
        {/*            <td>1,480</td>*/}
        {/*            <td>*/}
        {/*              <div className="d-flex align-items-center">*/}
        {/*                <span className="mr-2">60%</span>*/}
        {/*                <div>*/}
        {/*                  <Progress*/}
        {/*                    max="100"*/}
        {/*                    value="60"*/}
        {/*                    barClassName="bg-gradient-danger"*/}
        {/*                  />*/}
        {/*                </div>*/}
        {/*              </div>*/}
        {/*            </td>*/}
        {/*          </tr>*/}
        {/*          <tr>*/}
        {/*            <th scope="row">Facebook</th>*/}
        {/*            <td>5,480</td>*/}
        {/*            <td>*/}
        {/*              <div className="d-flex align-items-center">*/}
        {/*                <span className="mr-2">70%</span>*/}
        {/*                <div>*/}
        {/*                  <Progress*/}
        {/*                    max="100"*/}
        {/*                    value="70"*/}
        {/*                    barClassName="bg-gradient-success"*/}
        {/*                  />*/}
        {/*                </div>*/}
        {/*              </div>*/}
        {/*            </td>*/}
        {/*          </tr>*/}
        {/*          <tr>*/}
        {/*            <th scope="row">Google</th>*/}
        {/*            <td>4,807</td>*/}
        {/*            <td>*/}
        {/*              <div className="d-flex align-items-center">*/}
        {/*                <span className="mr-2">80%</span>*/}
        {/*                <div>*/}
        {/*                  <Progress max="100" value="80" />*/}
        {/*                </div>*/}
        {/*              </div>*/}
        {/*            </td>*/}
        {/*          </tr>*/}
        {/*          <tr>*/}
        {/*            <th scope="row">Instagram</th>*/}
        {/*            <td>3,678</td>*/}
        {/*            <td>*/}
        {/*              <div className="d-flex align-items-center">*/}
        {/*                <span className="mr-2">75%</span>*/}
        {/*                <div>*/}
        {/*                  <Progress*/}
        {/*                    max="100"*/}
        {/*                    value="75"*/}
        {/*                    barClassName="bg-gradient-info"*/}
        {/*                  />*/}
        {/*                </div>*/}
        {/*              </div>*/}
        {/*            </td>*/}
        {/*          </tr>*/}
        {/*          <tr>*/}
        {/*            <th scope="row">twitter</th>*/}
        {/*            <td>2,645</td>*/}
        {/*            <td>*/}
        {/*              <div className="d-flex align-items-center">*/}
        {/*                <span className="mr-2">30%</span>*/}
        {/*                <div>*/}
        {/*                  <Progress*/}
        {/*                    max="100"*/}
        {/*                    value="30"*/}
        {/*                    barClassName="bg-gradient-warning"*/}
        {/*                  />*/}
        {/*                </div>*/}
        {/*              </div>*/}
        {/*            </td>*/}
        {/*          </tr>*/}
        {/*        </tbody>*/}
        {/*      </Table>*/}
        {/*    </Card>*/}
        {/*  </Col>*/}
        {/*</Row>*/}
      </Container>
    </>
  );
};

export default Index;
